class CompleteTheLookVariants extends HTMLElement {
  constructor() {
    super();
    this.options = JSON.parse(this.dataset.options || '[]');
    const variantsScript = this.querySelector('[data-variants-json]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    this.ctlId = this.dataset.ctlId;
    this.form = document.getElementById(`product-form-${this.ctlId}`);
    if (!this.form) return;

    this.variantInput = this.form.querySelector('[data-ctl-variant-input]');
    this.priceContainer = document.getElementById(`price-${this.ctlId}`);
    this.mainImage = document.querySelector(`#CompleteTheLook-${this.ctlId} [data-ctl-main-image]`);
    this.submitBtn = this.form.querySelector('[data-ctl-submit]');
    this.submitText = this.form.querySelector('[data-ctl-submit-text]');
    this.optionLabels = this.querySelectorAll('[data-ctl-option-label]');

    this.addEventListener('change', this.onVariantChange.bind(this));
    this.updateAvailableSelects();
  }

  getAvailableValues() {
    const values = {};
    for (const i in this.options) {
      const optName = this.options[i];
      if (!values[optName]) values[optName] = [];
      for (const v of this.variants) {
        const val = v.options[i];
        if (val && !values[optName].includes(val)) values[optName].push(val);
      }
    }
    return values;
  }

  getSelectableValues(availableValues, selected) {
    const values = Object.assign({}, availableValues);
    const selectedOptions = Object.keys(selected);
    if (!selectedOptions.length) return values;

    const isVariantAvailable = (v) => {
      if (v.available) return true;
      const preOrder = v.metafields?.find((m) => m.key === 'pre_order_variant');
      return preOrder?.value && preOrder.value.available && v.pre_order_enabled;
    };

    for (const selectedOption of selectedOptions) {
      const selectedValue = selected[selectedOption];
      const otherOptions = this.options.filter((o) => o !== selectedOption);
      if (otherOptions.length) {
        for (const otherOption of otherOptions) {
          values[otherOption] = values[otherOption].filter((val) =>
            this.variants.some(
              (v) =>
                v.options.indexOf(val) !== -1 &&
                v.options.indexOf(selectedValue) !== -1 &&
                isVariantAvailable(v)
            )
          );
        }
      } else {
        values[selectedOption] = values[selectedOption].filter((val) =>
          this.variants.some((v) => {
            if (this.options.length === 1) {
              return v.options.indexOf(val) !== -1 && isVariantAvailable(v);
            }
            return v.options.indexOf(val) !== -1 && v.options.indexOf(selectedValue) !== -1 && isVariantAvailable(v);
          })
        );
      }
    }
    return values;
  }

  updateAvailableSelects() {
    const selected = {};
    this.querySelectorAll('.product-form__value').forEach((fieldset) => {
      const radio = fieldset.querySelector('input:checked');
      if (radio) selected[radio.dataset.optionName] = radio.value;
    });
    const availableValues = this.getAvailableValues();
    const selectableValues = this.getSelectableValues(availableValues, selected);
    this.querySelectorAll('.product-form__value').forEach((fieldset) => {
      const optionName = fieldset.dataset.optionName;
      const inputs = fieldset.querySelectorAll('input');
      const selectable = selectableValues[optionName] || [];
      inputs.forEach((input) => {
        if (selectable.includes(input.value)) {
          input.classList.remove('disabled');
        } else {
          input.classList.add('disabled');
        }
      });
    });
  }

  getSelectedOptions() {
    const opts = {};
    this.querySelectorAll('.product-form__value').forEach((fieldset) => {
      const radio = fieldset.querySelector('input:checked');
      if (radio) opts[radio.dataset.optionName] = radio.value;
    });
    return opts;
  }

  findVariant(opts) {
    return this.variants.find((v) => {
      return this.options.every((optName, i) => v.options[i] === opts[optName]);
    });
  }

  onVariantChange() {
    const opts = this.getSelectedOptions();
    const variant = this.findVariant(opts);

    this.updateAvailableSelects();

    if (!variant) {
      this.submitBtn?.setAttribute('disabled', '');
      if (this.submitText) this.submitText.textContent = window.variantStrings?.unavailable ?? 'Unavailable';
      return;
    }

    this.variantInput.value = variant.id;
    this.updatePrice(variant);
    this.updateImage(variant);
    this.updateButton(variant);
    this.updateOptionLabels(variant);
  }

  updatePrice(variant) {
    if (!this.priceContainer || !variant) return;

    const formatMoney = (cents) => (window.Shopify?.formatMoney ? window.Shopify.formatMoney(cents) : (cents / 100).toFixed(0));

    const price = variant.price;
    const compareAt = variant.compare_at_price;
    const regularEl = this.priceContainer.querySelector('.price__regular .price-item--regular');
    const saleRegularEl = this.priceContainer.querySelector('.price__sale .price-item--regular');
    const salePriceEl = this.priceContainer.querySelector('.price__sale .price-item--sale');
    const badgeEl = this.priceContainer.querySelector('.price__badge-sale');

    this.priceContainer.classList.remove('price--on-sale', 'price--sold-out');
    if (!variant.available) this.priceContainer.classList.add('price--sold-out');
    if (compareAt && compareAt > price) this.priceContainer.classList.add('price--on-sale');

    if (regularEl) regularEl.textContent = formatMoney(price);
    if (saleRegularEl) saleRegularEl.textContent = formatMoney(compareAt || price);
    if (salePriceEl) salePriceEl.textContent = formatMoney(price);
    if (badgeEl) {
      const save = compareAt ? compareAt - price : 0;
      badgeEl.textContent = save > 0 ? `Save ${formatMoney(save)}` : '';
    }
  }

  updateImage(variant) {
    if (!this.mainImage || !variant?.featured_media) return;
    const media = variant.featured_media;
    const img = media.preview_image || media;
    const src = img.src || img.url;
    if (src) {
      this.mainImage.src = src;
      this.mainImage.srcset = src;
    }
    this.mainImage.alt = media.alt || '';
  }

  updateButton(variant) {
    if (!this.submitBtn || !this.submitText) return;
    if (variant.available) {
      this.submitBtn.removeAttribute('disabled');
      this.submitText.textContent = window.variantStrings?.addToCart ?? 'Add to cart';
    } else {
      this.submitBtn.setAttribute('disabled', '');
      this.submitText.textContent = window.variantStrings?.soldOut ?? 'Sold out';
    }
  }

  updateOptionLabels(variant) {
    if (!variant || !this.optionLabels?.length) return;
    variant.options.forEach((opt, i) => {
      if (this.optionLabels[i]) this.optionLabels[i].textContent = opt;
    });
  }
}

customElements.define('complete-the-look-variants', CompleteTheLookVariants);
